import { Injectable } from '@angular/core';
import { InventoryDataProvider } from '../tokens';
import { OfflineStateService } from './offline-state.service';
import { OfflineSequenceService } from './offline-sequence.service';
import type {
  ProductFilterParams, OfflineProduct, OfflineStock,
  CreateMovementData, OfflineWarehouse, SuccessResult,
} from '../types/offline-models';

@Injectable({ providedIn: 'root' })
export class OfflineInventoryService implements InventoryDataProvider {
  constructor(
    private offlineState: OfflineStateService,
    private sequenceService: OfflineSequenceService,
  ) {}

  async getProducts(params: ProductFilterParams): Promise<OfflineProduct[]> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');
    const selector = params?.isActive !== undefined ? { isActive: params.isActive } : {};
    const docs = await db.products.find({ selector }).exec();
    return docs.map(d => d.toJSON() as OfflineProduct);
  }

  async getStocks(warehouseId: number): Promise<OfflineStock[]> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');
    const docs = await db.stocks.find({ selector: { warehouseId } }).exec();
    return docs.map(d => d.toJSON() as OfflineStock);
  }

  async createMovement(data: CreateMovementData): Promise<SuccessResult> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');

    const movementNumber = await this.sequenceService.generateStockMovementNumber(data.userId);
    const now = new Date().toISOString();

    await db.stock_movements.insert({
      id: crypto.randomUUID(),
      productId: data.productId,
      fromWarehouseId: data.fromWarehouseId ?? null,
      toWarehouseId: data.toWarehouseId ?? null,
      quantity: data.quantity,
      variantId: null,
      batchNumber: null,
      type: data.type,
      reference: movementNumber,
      notes: null,
      unitId: null,
      companyId: data.companyId,
      offlineId: crypto.randomUUID(),
      syncStatus: 'PENDING',
      createdAt: now,
      createdBy: data.userId,
    });

    await this.offlineState.refreshPendingCount();
    return { success: true, reference: movementNumber };
  }

  async getWarehouses(): Promise<OfflineWarehouse[]> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');
    const docs = await db.warehouses.find({ selector: { isActive: true } }).exec();
    return docs.map(d => d.toJSON() as OfflineWarehouse);
  }
}
